/** What each action takes, browser-safe so the console's wire is derived from it. */

import { z } from 'zod';
import { DEFAULT_BOOK } from '../config.ts';
import { instrumentRefSchema, trackedContractSchema, underlyingSchema } from './instrument.ts';
import {
  ANALYST_STATUSES,
  analystBioSchema,
  analystIdSchema,
  analystNameSchema,
  FOLLOW_STATUSES,
  followIdSchema,
  followInviteIdSchema,
} from './objects/analyst.ts';
import {
  bookDescriptionSchema,
  bookIdSchema,
  bookSchema,
  limitMarginSchemas,
  TELEGRAM_KINDS,
} from './objects/book.ts';
import {
  alertFloorsSchema,
  channelIdSchema,
  channelLabelSchema,
  telegramChatIdSchema,
} from './objects/channel.ts';
import { feedbackBodySchema } from './objects/feedback.ts';
import { closedSaidSchema, filingSchema, TYPED_IN } from './objects/filing.ts';
import {
  bookTermsSchema,
  emailSchema,
  MEMBER_STATUSES,
  memberIdSchema,
  memberNameSchema,
  namesATerm,
} from './objects/member.ts';
import { stepSchema } from './objects/path.ts';
import { quantitySchema } from './objects/position.ts';
import {
  CLOSABLE_RECOMMENDATION_STATUSES,
  NOTHING_TO_EDIT,
  NOTHING_TO_REVISE,
  recommendedOnSchema,
} from './objects/recommendation.ts';
import { externalIdSchema } from './objects/recommendation-ref.ts';
import { DECISIONS } from './objects/review.ts';
import { NO_TERM_GIVEN, namesAnOverride, termOverridesSchema } from './objects/terms.ts';
import { orderIdSchema, reasonSchema, tagSchema } from './order.ts';
import { idSchema, PRICE_BASES, priceSchema, TRADE_TYPES } from './primitives.ts';

export const addTelegramChannelInput = z.object({
  analystId: analystIdSchema,
  /** Negative for Telegram channels. Nothing is heard until the desk's Telegram account joins it. */
  telegramChatId: telegramChatIdSchema,
  label: channelLabelSchema.nullish().catch(null),
});

export const applyAnalystInput = z.object({
  memberId: memberIdSchema,
  telegramChatId: telegramChatIdSchema,
  name: analystNameSchema.nullish().catch(null),
  bio: analystBioSchema.nullish().catch(null),
});

export const archiveBookInput = z.object({ bookId: bookIdSchema });

export const claimIdentityInput = z.object({
  memberId: memberIdSchema,
  googleSub: z.string().trim().min(1),
  name: memberNameSchema.nullable().catch(null),
  picture: z.url().nullable().catch(null),
});

export const clearStopInput = z.object({
  positionId: idSchema,
  // The stop the caller saw: one parked since, by a hand beside it, is not the one it meant.
  stopId: idSchema,
  reason: reasonSchema,
});

export const closeRecommendationInput = z.object({
  recommendationId: idSchema,
  /** Null where the status carries its own: a target or stop close settles at that level. */
  price: priceSchema.nullable(),
  status: z.enum(CLOSABLE_RECOMMENDATION_STATUSES),
  said: closedSaidSchema.nullish(),
});

export const createAnalystInput = z.object({
  name: analystNameSchema,
  bio: analystBioSchema.nullish().catch(null),
  /** Absent lists the analyst unclaimed. */
  memberId: memberIdSchema.nullish(),
});

export const createPositionInput = z.object({
  recommendationId: idSchema,
  memberId: idSchema,
  instrument: instrumentRefSchema,
  entryPrice: priceSchema,
  quantity: quantitySchema,
  /** The tracked contract's price the ticket measured the stop from, where the leg is another. */
  judged: priceSchema.optional(),
  exitsWithRecommendation: z.boolean().default(true),
});

export const createRecommendationInput = z.object({
  analystId: analystIdSchema,
  book: bookSchema.default(DEFAULT_BOOK),
  underlying: underlyingSchema,
  tracking: trackedContractSchema,
  tradeType: z.enum(TRADE_TYPES),
  entryPrice: priceSchema,
  targetPrice: priceSchema,
  stoplossPrice: priceSchema,
  externalId: externalIdSchema.optional(),
  /** Market price read before the write. Optional so a silent feed cannot block filing. */
  filedPrice: priceSchema.optional(),
  recommendedOn: recommendedOnSchema.optional(),
  filed: filingSchema.default(TYPED_IN),
  /** A chart's levels, moved off the month it drew onto the one tracked. */
  drawn: z.object({ tradingsymbol: z.string().min(1), basis: z.number() }).optional(),
});

export const decideAccessInput = z.object({
  memberId: memberIdSchema,
  decision: z.enum(DECISIONS),
  expectedStatus: z.enum(MEMBER_STATUSES).optional(),
});

export const decideAnalystInput = z.object({
  analystId: analystIdSchema,
  decision: z.enum(DECISIONS),
  /** Status the page showed when loaded; a stale page is refused. */
  expectedStatus: z.enum(ANALYST_STATUSES).optional(),
});

export const decideFollowInput = z.object({
  followId: followIdSchema,
  decision: z.enum(DECISIONS),
  /** Status the page showed when loaded; a stale page is refused. */
  expectedStatus: z.enum(FOLLOW_STATUSES).optional(),
});

export const editAnalystFields = z.object({
  analystId: analystIdSchema,
  name: analystNameSchema.optional(),
  bio: analystBioSchema.nullable().optional(),
  /** Set once, never cleared: a claimed analyst is not handed back to the desk. */
  memberId: memberIdSchema.optional(),
  /** False takes them out of the directory: found then only by following, on their invites. */
  listed: z.boolean().optional(),
});

export const editAnalystInput = editAnalystFields.refine(
  (asked) =>
    asked.name !== undefined ||
    asked.bio !== undefined ||
    asked.memberId !== undefined ||
    asked.listed !== undefined,
  {
    message: 'nothing to change — give a name, a bio, a member to link, or whether they are listed',
  },
);

export const editBookInput = z
  .object({
    bookId: bookIdSchema,
    name: bookSchema.optional(),
    description: bookDescriptionSchema.nullable().optional(),
    /** Null hands the margin back to the desk's; absent leaves it. */
    limitMarginBps: limitMarginSchemas.limitMarginBps.nullable().optional(),
    optionLimitMarginBps: limitMarginSchemas.optionLimitMarginBps.nullable().optional(),
    /** Every kind of Telegram post that files here when it names no book; the rest go elsewhere. */
    telegram: z.array(z.enum(TELEGRAM_KINDS)).optional(),
  })
  .refine(
    (asked) =>
      [
        asked.name,
        asked.description,
        asked.limitMarginBps,
        asked.optionLimitMarginBps,
        asked.telegram,
      ].some((said) => said !== undefined),
    { message: 'nothing to change — give the book a new name or a setting' },
  );

export const editPositionInput = z
  .object({
    positionId: idSchema,
    memberId: idSchema,
    entryPrice: priceSchema.optional(),
    quantity: quantitySchema.optional(),
  })
  .refine((v) => v.entryPrice !== undefined || v.quantity !== undefined, {
    message: 'nothing to edit — give a new entry price and/or quantity',
  });

export const editRecommendationInput = z
  .object({
    recommendationId: idSchema,
    entryPrice: priceSchema.optional(),
    targetPrice: priceSchema.optional(),
    stoplossPrice: priceSchema.optional(),
    externalId: externalIdSchema.optional(),
    recommendedOn: recommendedOnSchema.optional(),
  })
  .refine(
    (v) =>
      v.entryPrice !== undefined ||
      v.targetPrice !== undefined ||
      v.stoplossPrice !== undefined ||
      v.externalId !== undefined ||
      v.recommendedOn !== undefined,
    { message: NOTHING_TO_EDIT },
  );

export const exitPositionInput = z.object({
  positionId: idSchema,
  memberId: idSchema,
  exitPrice: priceSchema,
  /** Units out of the fill, whole lots; the rest stays open. Omitted, all of it. */
  quantity: z.number().int().positive().optional(),
  /** Kite's row says the fallback fired and its order sold this. */
  fallbackFired: z.literal(true).optional(),
});

export const carryPositionInput = z.object({
  positionId: idSchema,
  recommendationId: idSchema,
  quantity: z.number().int().positive(),
});

export const setOwnLevelsInput = z.object({
  positionId: idSchema,
  memberId: idSchema,
  ownStop: priceSchema.nullable(),
  ownTarget: priceSchema.nullable(),
  trails: z.boolean(),
  /** The tracked series now, which neither level may already be past. */
  price: priceSchema,
});

export const setExitWithRecommendationInput = z.object({
  positionIds: z.array(idSchema).min(1).max(32),
  memberId: idSchema,
  exitsWithRecommendation: z.boolean(),
});

export const expireRecommendationInput = z.object({
  recommendationId: idSchema,
  /** The contract's last live price, else its last close: never the price it was filed at. */
  price: priceSchema,
});

export const mintInviteInput = z.object({ memberId: memberIdSchema });

export const reachStepInput = z.object({ memberId: memberIdSchema, step: stepSchema });

export const mintWebhookTokenInput = z.object({
  analystId: analystIdSchema,
  /** Rotate this channel's token in place; absent mints a new channel. */
  channelId: channelIdSchema.nullish(),
  label: channelLabelSchema.nullish().catch(null),
  /** Ignored on rotate: only `edit-book` renames a channel's book. */
  book: bookSchema.nullish().catch(null),
  /** Ignored on rotate, like the book: `set-channel-filing` changes them. */
  ...alertFloorsSchema.partial().shape,
});

export const openBookInput = z.object({
  analystId: analystIdSchema,
  book: bookSchema,
  description: bookDescriptionSchema.optional(),
});

const ended = z.enum(['CANCELLED', 'REFUSED']);

export const recordOrderInput = z.discriminatedUnion('kind', [
  z.discriminatedUnion('outcome', [
    z.object({
      kind: z.literal('ENTRY'),
      positionId: idSchema,
      outcome: z.literal('LIVE'),
      brokerOrderId: orderIdSchema,
    }),
    z.object({
      kind: z.literal('ENTRY'),
      positionId: idSchema,
      outcome: z.literal('FILLED'),
      brokerOrderId: orderIdSchema,
      filledQuantity: quantitySchema,
      filledPrice: priceSchema,
    }),
    z.object({
      kind: z.literal('ENTRY'),
      positionId: idSchema,
      outcome: ended,
      reason: reasonSchema,
    }),
  ]),
  z.discriminatedUnion('outcome', [
    z.object({
      kind: z.literal('STOP'),
      positionId: idSchema,
      outcome: z.literal('LIVE'),
      triggerId: idSchema,
      triggerPrice: priceSchema,
    }),
    // FILLED only from Kite's own trigger row saying it fired and sold, never inferred.
    z.object({
      kind: z.literal('STOP'),
      positionId: idSchema,
      outcome: z.enum(['FILLED', 'CANCELLED', 'REFUSED']),
      reason: reasonSchema.optional(),
    }),
  ]),
  z.discriminatedUnion('outcome', [
    z.object({
      kind: z.literal('EXIT'),
      positionId: idSchema,
      outcome: z.literal('FILLED'),
      exitPrice: priceSchema,
    }),
    z.object({
      kind: z.literal('EXIT'),
      positionId: idSchema,
      outcome: ended,
      reason: reasonSchema,
    }),
  ]),
]);

export const recordPriceInput = z.object({
  recommendationId: idSchema,
  price: priceSchema,
  /** Only the watcher knows candle closes and sends `close`. */
  basis: z.enum(PRICE_BASES).default('touch'),
});

export const refileRecommendationInput = z.object({ recommendationId: idSchema, book: bookSchema });

/** That the session is an admin's, and the member none, is checked at the surface. */
export const viewAsInput = z.object({ sessionId: idSchema, memberId: memberIdSchema });

export const stopViewingAsInput = z.object({ sessionId: idSchema });

/** Which account is meant is confirmed at the surface: the address typed back is never logged. */
export const deleteAccountInput = z.object({ memberId: memberIdSchema });

export const removeAnalystInput = z.object({
  analystId: analystIdSchema,
  confirmName: analystNameSchema,
});

export const removeBookInput = z.object({ bookId: bookIdSchema });

export const removeChannelInput = z.object({ channelId: channelIdSchema });

export const removePositionInput = z.object({
  positionId: idSchema,
  memberId: idSchema,
});

export const removeRecommendationInput = z.object({ recommendationId: idSchema });

/** Who is signing in, as Google said: they may have no member row yet. */
const arrivingInput = z.object({
  email: emailSchema,
  googleSub: z.string().trim().min(1).nullish().catch(null),
  name: memberNameSchema.nullish().catch(null),
  picture: z.url().nullish().catch(null),
});

export const requestAccessInput = arrivingInput.extend({
  /** The member whose link they came on, resolved from its code outside, so the code is never logged. */
  inviterId: memberIdSchema.nullish(),
});

export const mintFollowInviteInput = z.object({
  analystId: analystIdSchema,
  email: emailSchema,
});

export const revokeFollowInviteInput = z.object({ inviteId: followInviteIdSchema });

/** The invite is resolved from its code outside, so the code is never logged. */
export const acceptFollowInviteInput = arrivingInput.extend({ inviteId: followInviteIdSchema });

export const requestFollowInput = z.object({
  memberId: memberIdSchema,
  analystId: analystIdSchema,
});

export const reviseRecommendationInput = z
  .object({
    recommendationId: idSchema,
    targetPrice: priceSchema.optional(),
    stoplossPrice: priceSchema.optional(),
  })
  .refine((v) => v.targetPrice !== undefined || v.stoplossPrice !== undefined, {
    message: NOTHING_TO_REVISE,
  });

export const rollRecommendationInput = z.object({
  recommendationId: idSchema,
  rollOutPrice: priceSchema,
  rollInPrice: priceSchema,
  contract: trackedContractSchema,
});

export const sendOrderInput = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('ENTRY'), positionId: idSchema, memberId: idSchema }),
  z.object({ kind: z.literal('STOP'), positionId: idSchema, triggerPrice: priceSchema }),
  z.object({ kind: z.literal('EXIT'), positionId: idSchema, tag: tagSchema }),
]);

export const setAutopilotPausedInput = z.object({
  memberId: memberIdSchema,
  paused: z.boolean(),
});

export const setBookTermsFields = z.object({
  memberId: memberIdSchema,
  bookId: bookIdSchema,
  ...bookTermsSchema.shape,
});

export const setBookTermsInput = setBookTermsFields.refine(namesATerm, { message: NO_TERM_GIVEN });

export const setChannelFilingInput = z.object({
  /** Passed in so the analyst is locked before the channel, the same order as `mint-webhook-token` (deadlock). */
  analystId: analystIdSchema,
  channelId: channelIdSchema,
  autoFile: z.boolean().optional(),
  ...alertFloorsSchema.partial().shape,
});

export const setMemberTermsFields = z.object({
  memberId: memberIdSchema,
  ...termOverridesSchema.shape,
});

export const setMemberTermsInput = setMemberTermsFields.refine(namesAnOverride, {
  message: NO_TERM_GIVEN,
});

export const setPlacingInput = z.object({
  memberId: memberIdSchema,
  enabled: z.boolean(),
});

export const submitFeedbackInput = z.object({
  memberId: idSchema,
  body: feedbackBodySchema,
});

export const clearBookTermsInput = z.object({ memberId: memberIdSchema });

export const unfollowInput = z.object({
  memberId: memberIdSchema,
  analystId: analystIdSchema,
});
